/**
 * store/index.ts
 * desc: setup client-side store
 * notes: also setup "redux-dev-tools". The dev-only immutability check is off: it re-walks the whole state tree on
 *        every action with no cache, and only immer-produced state is frozen and skipped, so the hand-written slices
 *        (path detail, step factories, source types…) cost it tens of ms per action — a debug run polls every second
 */

import reducers from './reducers';
import { combineReducers, configureStore, type ThunkAction, type UnknownAction } from '@reduxjs/toolkit';
import { api } from './api';
import { setAnalyticsStore } from '../utilities/analytics';

// Add API reducer
const allReducers = {
    ...reducers,
    [api.reducerPath]: api.reducer,
};

const rootReducer = combineReducers(allReducers);

export type RootState = ReturnType<typeof rootReducer>;

export const setupStore = (preloadedState?: Partial<RootState>) => {
    const knownKeys = Object.keys(allReducers);
    const filteredState = preloadedState
        ? Object.fromEntries(Object.entries(preloadedState).filter(([ key ]) => knownKeys.includes(key))) as Partial<RootState>
        : preloadedState;

    return configureStore({
        reducer: rootReducer,
        middleware: (getDefaultMiddleware) => getDefaultMiddleware({ immutableCheck: false }).concat(api.middleware),
        preloadedState: filteredState
    });
};

export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];
// return type of a legacy hand-written thunk (store/actions/*)
export type AppThunk<R = void> = ThunkAction<R, RootState, unknown, UnknownAction>;

// initialize
const store = setupStore();
setAnalyticsStore(store);


export default store;
